import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, TrendingUp, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input.tsx';
import { getLtvPages, getLtvSummary, getLtvDomains, getLtvCountries, getLtvCategories, getLtvInteraction } from '@/services/api/ltv';
import type { LtvPage, LtvLead, SortOrder } from '@/types/ltv';
import { money, num, dec, countryName, SortHeader, toggleSort, Spinner, Empty, Pagination } from './shared';
import { KpiCards } from './KpiCards';
import { Sparkline } from './Sparkline';
import PagePanel from './PagePanel';
import { CompactDateRangePicker } from '@/pages/Dashboard/components/CompactDateRangePicker';
import { SearchableSelect } from '@/components/ui/searchable-select';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const TODAY = iso(new Date());
const WEEK_AGO = iso(new Date(Date.now() - 6 * 86400000));

const trendUp = (t?: number[]) => !!t && t.length >= 2 && t[t.length - 1] >= t[0];

export default function Ltv() {
    const [startDate, setStartDate] = useState(WEEK_AGO);
    const [endDate, setEndDate] = useState(TODAY);
    const [q, setQ] = useState('');
    const [sortBy, setSortBy] = useState('revenue_page');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
    const [offset, setOffset] = useState(0);
    // filtros do topo — escopam KPIs, páginas e drill
    const [domainFilter, setDomainFilter] = useState<string | null>(null);
    const [countryFilter, setCountryFilter] = useState<string | null>(null);
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    // navegação (breadcrumb)
    const [page, setPage] = useState<LtvPage | null>(null);
    const [byDomain, setByDomain] = useState(true);
    const [domain, setDomain] = useState<string | null>(null);
    const [selectedLead, setSelectedLead] = useState<LtvLead | null>(null);
    const limit = 20;

    const period = { start_date: startDate, end_date: endDate };
    const domainParam = domainFilter || undefined;
    const countryParam = countryFilter || undefined;
    const categoryParam = categoryFilter || undefined;
    const filters = { domain: domainParam, country: countryParam, category_id: categoryParam };
    const params = { ...period, ...filters, sort_by: sortBy, sort_order: sortOrder, q: q || undefined, limit, offset };

    const domainListQ = useQuery({
        queryKey: ['ltv-domain-list', startDate, endDate],
        queryFn: () => getLtvDomains(period),
        staleTime: 60000,
    });
    const countryListQ = useQuery({
        queryKey: ['ltv-country-list', startDate, endDate],
        queryFn: () => getLtvCountries(period),
        staleTime: 60000,
    });
    const categoryListQ = useQuery({
        queryKey: ['ltv-category-list'],
        queryFn: () => getLtvCategories(),
        staleTime: 300000,
    });

    const summaryQ = useQuery({
        queryKey: ['ltv-summary', startDate, endDate, domainFilter, countryFilter, categoryFilter],
        queryFn: () => getLtvSummary({ ...period, ...filters }),
        placeholderData: (p) => p,
        staleTime: 60000,
    });

    const interactionQ = useQuery({
        queryKey: ['ltv-interaction', startDate, endDate, domainFilter, countryFilter, categoryFilter],
        queryFn: () => getLtvInteraction({ ...period, ...filters }),
        placeholderData: (p) => p,
        staleTime: 120000,
    });

    const pagesQ = useQuery({
        queryKey: ['ltv-pages', startDate, endDate, domainFilter, countryFilter, categoryFilter, sortBy, sortOrder, q, offset],
        queryFn: () => getLtvPages(params),
        placeholderData: (p) => p,
        staleTime: 60000,
        enabled: page === null,
    });

    const domainOptions = [
        { value: '', label: 'Todos os domínios' },
        ...(domainListQ.data ?? []).map((d) => ({ value: d, label: d })),
    ];
    const countryOptions = [
        { value: '', label: 'Todos os países' },
        ...(countryListQ.data ?? []).map((c) => ({ value: c, label: countryName(c) })),
    ];
    const categoryOptions = [
        { value: '', label: 'Todas as categorias' },
        ...(categoryListQ.data ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    ];

    const rows: LtvPage[] = pagesQ.data?.data ?? [];
    const total = pagesQ.data?.meta?.total ?? 0;

    const onSort = (field: string) => {
        const s = toggleSort(field, sortBy, sortOrder);
        setSortBy(s.sort_by); setSortOrder(s.sort_order); setOffset(0);
    };
    const onDateChange = (s: string, e: string) => {
        setStartDate(s); setEndDate(e); setOffset(0);
        setPage(null); setDomain(null); setSelectedLead(null);
    };
    const resetDrill = () => { setOffset(0); setPage(null); setDomain(null); setSelectedLead(null); };
    const onDomainFilterChange = (v: string) => { setDomainFilter(v || null); resetDrill(); };
    const onCountryFilterChange = (v: string) => { setCountryFilter(v || null); resetDrill(); };
    const onCategoryFilterChange = (v: string) => { setCategoryFilter(v || null); resetDrill(); };

    const openPage = (p: LtvPage) => { setPage(p); setByDomain(true); setDomain(null); setSelectedLead(null); };
    const goPages = () => { setPage(null); setDomain(null); setSelectedLead(null); };
    const goPage = () => { setDomain(null); setSelectedLead(null); };
    const onBack = () => { if (!domainFilter && domain) goPage(); else goPages(); };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-blue-600" /> Relatório de LTV
                    </h1>
                    <p className="text-sm text-gray-400 mt-0.5">Receita aproximada por lead, por página e por domínio. <span className="text-gray-300">Valores em US$ (GAM).</span></p>
                </div>
            </div>

            {/* Cards de KPI */}
            <KpiCards summary={summaryQ.data} isLoading={summaryQ.isLoading} isFetching={summaryQ.isFetching}
                interaction={interactionQ.data} interactionLoading={interactionQ.isLoading} />

            {/* Breadcrumb + período */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                    <button onClick={goPages}
                        className={page ? 'text-blue-600 hover:underline font-medium' : 'font-bold text-gray-900 bg-gray-100 px-3 py-1.5 rounded-lg'}>
                        Páginas
                    </button>
                    {page && (
                        <>
                            <ChevronRight className="w-4 h-4 text-gray-300" />
                            <button onClick={goPage}
                                className={domain ? 'text-blue-600 hover:underline font-medium truncate max-w-[220px]' : 'font-bold text-gray-900 bg-gray-100 px-3 py-1.5 rounded-lg truncate max-w-[260px]'}>
                                {page.page_name || page.p_id}
                            </button>
                        </>
                    )}
                    {page && domain && !domainFilter && (
                        <>
                            <ChevronRight className="w-4 h-4 text-gray-300" />
                            <span className="font-bold text-gray-900 bg-gray-100 px-3 py-1.5 rounded-lg">{domain}</span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    <SearchableSelect value={domainFilter ?? ''} onValueChange={onDomainFilterChange}
                        options={domainOptions} placeholder="Todos os domínios" searchPlaceholder="Buscar domínio..."
                        className="w-48" />
                    <SearchableSelect value={countryFilter ?? ''} onValueChange={onCountryFilterChange}
                        options={countryOptions} placeholder="Todos os países" searchPlaceholder="Buscar país..."
                        className="w-40" />
                    <SearchableSelect value={categoryFilter ?? ''} onValueChange={onCategoryFilterChange}
                        options={categoryOptions} placeholder="Todas as categorias" searchPlaceholder="Buscar categoria..."
                        className="w-44" />
                    <CompactDateRangePicker startDate={startDate} endDate={endDate} onDateChange={onDateChange} />
                </div>
            </div>

            {/* Conteúdo: lista de páginas OU drill-down */}
            {page === null ? (
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Páginas</h2>
                            <p className="text-xs text-gray-400 mt-0.5">{total.toLocaleString('pt-BR')} páginas com receita no período · clique para ver os leads</p>
                        </div>
                        <div className="relative w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                            <Input value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }}
                                placeholder="Buscar por p_id..."
                                className="pl-9 h-9 rounded-lg border-gray-200 text-sm bg-gray-50 focus:bg-white" />
                            {q && <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>}
                        </div>
                    </div>

                    {pagesQ.isLoading && !pagesQ.data ? (
                        <Spinner />
                    ) : rows.length === 0 ? (
                        <Empty label="Nenhuma página com receita nesse período. (o rollup já rodou pra essas datas?)" />
                    ) : (
                        <div className={pagesQ.isPlaceholderData ? 'opacity-60' : ''}>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[1280px]">
                                    <thead><tr className="border-y border-gray-100 bg-gray-50/50">
                                        <SortHeader label="Página" field="p_id" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                                        <SortHeader label="Receita" field="revenue_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Pageviews" field="pv_total_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Impr. GAM" field="impressions_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Clicks GAM" field="gam_clicks" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Clicks" field="clicks" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Leads (per.)" field="lead_count" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Leads totais" field="page_leads_total" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Interação" field="avg_interaction_days" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="Receita/Lead" field="avg_revenue_per_lead" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <SortHeader label="eCPM" field="ecpm_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Tendência</th>
                                    </tr></thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {rows.map((r) => (
                                            <tr key={r.p_id} onClick={() => openPage(r)} className="cursor-pointer hover:bg-gray-50/60 transition-colors">
                                                <td className="px-4 py-3">
                                                    <p className="font-medium text-gray-900 leading-tight flex items-center gap-1.5">
                                                        <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                                                        {r.page_name || r.p_id}
                                                    </p>
                                                    {r.page_name && <p className="text-[11px] text-gray-400 mt-0.5 ml-5 font-mono">{r.p_id}</p>}
                                                </td>
                                                <td className="px-4 py-3 text-right font-semibold text-emerald-600">{money(r.revenue_page)}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{num(r.pv_total_page)}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{num(r.impressions_page)}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{num(r.gam_clicks ?? 0)}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{num(r.clicks)}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{num(r.lead_count)}</td>
                                                <td className="px-4 py-3 text-right text-gray-900 font-medium">{num(r.page_leads_total ?? 0)}</td>
                                                <td className="px-4 py-3 text-right text-gray-600">{dec(r.avg_interaction_days ?? 0)} <span className="text-gray-400">d</span></td>
                                                <td className="px-4 py-3 text-right text-gray-700">{money(r.avg_revenue_per_lead)}</td>
                                                <td className="px-4 py-3 text-right text-gray-500">{dec(r.ecpm_page)}</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex justify-end">
                                                        <Sparkline data={r.trend} width={84} height={26} color={trendUp(r.trend) ? '#10b981' : '#94a3b8'} />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-4 py-3"><Pagination total={total} limit={limit} offset={offset} onOffset={setOffset} /></div>
                        </div>
                    )}
                </div>
            ) : (
                <PagePanel
                    page={page}
                    byDomain={byDomain} onToggleByDomain={(v) => { setByDomain(v); setDomain(null); setSelectedLead(null); }}
                    domain={domain} onPickDomain={(d) => { setDomain(d); setSelectedLead(null); }}
                    lockedDomain={domainFilter} country={countryParam}
                    selectedLead={selectedLead} onSelectLead={setSelectedLead}
                    onBack={onBack}
                    start_date={startDate} end_date={endDate}
                />
            )}
        </div>
    );
}
